import React, { useEffect, useState } from "react";
import {
  Button,
  Modal,
  Form,
  Row,
  Col,
  Alert,
  Tabs,
  Tab,
} from "react-bootstrap";
import ozowLogo from "../../Resources/ozow_logo.png";
import yocoLogo from "../../Resources/yoco_logo.png";
import banksLogo from "../../Resources/banks_logo.jpg";

import { FaCheck, FaTimes, FaUpload } from "react-icons/fa";
import { getPracticesByNumbers } from "../../api";
import { createBankingDetails } from "../../afinanceapi";
import YocoConnect from "./YocoConnect";
import { jwtDecode } from "jwt-decode";

function AddBankingModal({ onBankingUpdate = () => {}, ...props }) {
  const [pracName, setPracName] = useState([]);
  const [selectedPractice, setSelectedPractice] = useState();
  const [successAlert, setSuccessAlert] = useState(false);
  const [closeAlert, setCloseAlert] = useState(false);
  const [user, setUser] = useState({});

  const [bank, setBank] = useState({
    bankName: "",
    branchCode: "",
    accountNumber: "",
    accountName: "",
    accountType: "",
  });

  const [ozow, setOzow] = useState({
    merchantCode: "",
    privateKey: "",
    apiKey: "",
    paymentLink: "",
    onlinePayment: "",
  });

  const [yoco, setYoco] = useState({
    merchantId: "",
    privateKey: "",
    apiKey: "",
    cardMachine: "",
    paymentLink: "",
    onlinePayment: "",
  });

  useEffect(() => {
    async function loadData() {
      const token = sessionStorage.getItem("user");
      if (token) {
        const decoded = jwtDecode(token);
        const practices = await getPracticesByNumbers(decoded.practice);
        setUser(decoded);
        setPracName(practices);
      }
    }
    loadData();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const response = await createBankingDetails({
        practiceNumber: selectedPractice,
        bank,
        ozow,
        yoco,
      });

      // No status check since response is just data
      setSuccessAlert(true);
      setTimeout(() => {
        setSuccessAlert(false);
        props.onHide();
        onBankingUpdate();
      }, 1000);
    } catch (error) {
      console.error("Error creating service:", error);
    }
  };

  function handleClose() {
    setCloseAlert(true);
  }

  const handleCancelNo = () => {
    setCloseAlert(false);
  };

  const handleCancelYes = () => {
    setCloseAlert(false);
    setBank({
      bankName: "",
      branchCode: "",
      accountNumber: "",
      accountName: "",
      accountType: "",
    });

    setOzow({
      merchantCode: "",
      privateKey: "",
      apiKey: "",
      paymentLink: "",
      onlinePayment: "",
    });

    setYoco({
      merchantId: "",
      privateKey: "",
      apiKey: "",
      cardMachine: "",
      paymentLink: "",
      onlinePayment: "",
    });
    props.onHide();
    setSelectedPractice();
  };

  return (
    <Modal
      {...props}
      size="lg"
      aria-labelledby="contained-modal-title-vcenter"
      centered
      onHide={handleClose}
    >
      <Modal.Header
        closeButton={!closeAlert && !successAlert}
        className="border-secondary"
      >
        <div className="w-100">
          <Modal.Title
            id="contained-modal-title-vcenter"
            className="text-primary"
          >
            Add Banking & Payment Details
          </Modal.Title>
        </div>
      </Modal.Header>
      {closeAlert && (
        <Alert
          variant="info"
          className="w-100 d-flex align-items-center justify-content-between fs-6 fw-normal"
        >
          <span>
            Are you sure you want to close? Changes will not be saved.
          </span>
          <div className="ms-auto d-flex">
            <Button
              variant="outline-primary"
              className="btn btn-outline-primary me-1"
              onClick={handleCancelYes}
            >
              <FaCheck />
            </Button>
            <Button
              variant="outline-secondary"
              className="btn btn-outline-secondary me-1"
              onClick={handleCancelNo}
            >
              <FaTimes />
            </Button>
          </div>
        </Alert>
      )}
      {successAlert && (
        <Alert
          variant="success"
          className="w-100 d-flex align-items-center justify-content-between fs-6 fw-normal"
        >
          <span>The bank details were added successfully!</span>
        </Alert>
      )}
      <Modal.Body>
        <Form onSubmit={handleSubmit}>
          {/* Practice selector */}
          <Row className="mb-3">
            <Form.Group as={Col} controlId="selectedPractice">
              <h6 className="text-primary">Practice</h6>
              <Form.Label className="text-muted">
                Each practice should be created with their own bank account
                details
              </Form.Label>
              <Form.Select
                value={selectedPractice || ""}
                onChange={(e) => setSelectedPractice(e.target.value)}
                required
              >
                <option value="" disabled>
                  -- Select Practice --
                </option>
                {pracName.map((p) => (
                  <option key={p.practiceNumber} value={p.practiceNumber}>
                    {p.practiceName} ({p.practiceNumber})
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Row>

          {/* Tabs */}
          <h6 className="text-primary">Banking Settings</h6>
          <Form.Label className="text-muted mb-2">
            All account details are encrypted and stored securely. Please ensure
            that the details are correct before submitting.
          </Form.Label>
          <Tabs
            defaultActiveKey="banking"
            id="service-tabs"
            className="mb-3"
            justify
          >
            {/* Banking tab */}
            <Tab
              eventKey="banking"
              title={
                <>
                  <img
                    src={banksLogo}
                    alt="banks"
                    style={{ height: "30px", marginRight: "6px" }}
                  />
                </>
              }
            >
              <Row className="mb-3">
                <Form.Group as={Col} controlId="bankName">
                  <Form.Label>Bank Name</Form.Label>
                  <Form.Control
                    name="bankName"
                    value={bank.bankName}
                    onChange={(e) =>
                      setBank({ ...bank, bankName: e.target.value })
                    }
                  />
                </Form.Group>

                <Form.Group as={Col} controlId="branchCode">
                  <Form.Label>Branch Code</Form.Label>
                  <Form.Control
                    name="branchCode"
                    value={bank.branchCode}
                    onChange={(e) =>
                      setBank({ ...bank, branchCode: e.target.value })
                    }
                  />
                </Form.Group>
              </Row>

              <Row className="mb-3">
                <Form.Group as={Col} controlId="accountNumber">
                  <Form.Label>Account Number</Form.Label>
                  <Form.Control
                    name="accountNumber"
                    value={bank.accountNumber}
                    onChange={(e) =>
                      setBank({ ...bank, accountNumber: e.target.value })
                    }
                  />
                </Form.Group>

                <Form.Group as={Col} controlId="accountName">
                  <Form.Label>Account Name</Form.Label>
                  <Form.Control
                    name="accountName"
                    value={bank.accountName}
                    onChange={(e) =>
                      setBank({ ...bank, accountName: e.target.value })
                    }
                  />
                </Form.Group>

                <Form.Group as={Col} controlId="accountType">
                  <Form.Label>Account Type</Form.Label>
                  <Form.Control
                    name="accountType"
                    value={bank.accountType}
                    onChange={(e) =>
                      setBank({ ...bank, accountType: e.target.value })
                    }
                  />
                </Form.Group>
              </Row>
            </Tab>

            {/* Ozow tab */}
            <Tab
              eventKey="ozow"
              title={
                <>
                  <img
                    src={ozowLogo}
                    alt="Ozow"
                    style={{ height: "30px", marginRight: "6px" }}
                  />
                </>
              }
            >
              <Row className="mb-2">
                <Form.Label className="text-muted">
                  Ozow is a payment gateway that allows you to accept payments
                  online and with payment links. If you have an account with
                  Ozow, please enter your details below.
                </Form.Label>
              </Row>

              <Row>
                <Form.Label>Ozow will be used for:</Form.Label>
              </Row>

              <Row className="mb-3">
                <Form.Check
                  type="checkbox"
                  label="Payment Links"
                  checked={ozow.paymentLink === "true"}
                  onChange={(e) =>
                    setOzow((prev) => ({
                      ...prev,
                      paymentLink: e.target.checked ? "true" : "",
                    }))
                  }
                  className="mb-1 ms-4"
                />
                <Form.Check
                  className="ms-4"
                  type="checkbox"
                  label="Online Payments"
                  checked={ozow.onlinePayment === "true"}
                  onChange={(e) =>
                    setOzow((prev) => ({
                      ...prev,
                      onlinePayment: e.target.checked ? "true" : "",
                    }))
                  }
                />
              </Row>

              <Row className="mb-3">
                <Form.Group as={Col} controlId="ozowMerchantCode">
                  <Form.Label>Merchant Code</Form.Label>
                  <Form.Control
                    value={ozow.merchantCode}
                    onChange={(e) =>
                      setOzow({ ...ozow, merchantCode: e.target.value })
                    }
                  />
                </Form.Group>

                <Form.Group as={Col} controlId="ozowApiKey">
                  <Form.Label>API Key</Form.Label>
                  <Form.Control
                    value={ozow.apiKey}
                    onChange={(e) =>
                      setOzow({ ...ozow, apiKey: e.target.value })
                    }
                  />
                </Form.Group>

                <Form.Group as={Col} controlId="ozowPrivateKey">
                  <Form.Label>Private Key</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={ozow.privateKey}
                    onChange={(e) =>
                      setOzow({ ...ozow, privateKey: e.target.value })
                    }
                  />
                </Form.Group>
              </Row>
            </Tab>

            {/* Yoco tab */}

            <Tab
              eventKey="yoco"
              title={
                <img
                  src={yocoLogo}
                  alt="Yoco"
                  style={{ height: "20px", marginRight: 6 }}
                />
              }
            >
              <YocoConnect practiceNumber={selectedPractice} />
            </Tab>
          </Tabs>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <div className="d-flex justify-content-center align-items-center">
          <Button
            variant="primary"
            className="px-5 py-2" // Adjust padding to make it longer
            type="submit"
            onClick={handleSubmit}
          >
            <FaUpload className="me-2" />
            Submit
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}

export function AddBanking({ onBankingUpdate }) {
  const [serviceModalShow, setServiceModalShow] = useState(false);

  return (
    <div>
      <Button
        variant="primary"
        className="me-2 w-100"
        onClick={() => setServiceModalShow(true)}
      >
        Add Banking & Payments
      </Button>
      <AddBankingModal
        show={serviceModalShow}
        onHide={() => setServiceModalShow(false)}
        onBankingUpdate={onBankingUpdate}
      />
    </div>
  );
}
